import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Breadcrumb from "component/common/breadcrumb";
import JustoForceLogoutModal from "component/common/JustoForceLogoutModal";
import PasswordResetEmailPreview from "component/common/PasswordResetEmailPreview";
import ForceLogoutEmailPreview from "component/common/ForceLogoutEmailPreview";
import strings from "translate/language";
import { apiCall, errorToast, successToast } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import { handleListResponse, seo } from "utils/utils";
import "./DefaultPassword.css";

const SCOPE_OPTIONS = [
  { value: "active_users", label: "Active users (not channel partners)" },
  { value: "channel_partners", label: "Channel partners and agents" },
  { value: "both", label: "Everyone (users + channel partners)" },
];

const PEOPLE_LIST_RENDER_LIMIT = 400;

const TASKS = [
  {
    id: "default",
    titleKey: "accessControlTaskDefault",
    descKey: "accessControlTaskDefaultDesc",
    icon: "fa-lock",
  },
  {
    id: "bulk",
    titleKey: "accessControlTaskBulk",
    descKey: "accessControlTaskBulkDesc",
    icon: "fa-users",
  },
  {
    id: "single",
    titleKey: "accessControlTaskSingle",
    descKey: "accessControlTaskSingleDesc",
    icon: "fa-user",
  },
];

const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  },
};

const buildLoginOption = (item, kind) => {
  const loginId = item?.login_id;
  if (!loginId) {
    return null;
  }
  const name =
    [item?.firstname, item?.lastname].filter(Boolean).join(" ").trim() ||
    item?.agent_name ||
    item?.company_name ||
    item?.user_name ||
    strings.notavailable;
  const email = item?.email || "";
  const role =
    item?.role_title || (kind === "cp" ? "Channel partner / agent" : "User");
  const label = [name, email, role].filter(Boolean).join(" · ");
  return {
    value: String(loginId),
    label,
    userName: name,
    email,
    mobile: item?.mobile || item?.primary_mobile || "",
  };
};

const DefaultPassword = () => {
  const uid = useId();
  const passwordId = `${uid}-password`;
  const confirmId = `${uid}-confirm`;
  const passwordHintId = `${uid}-password-hint`;
  const passwordErrorId = `${uid}-password-error`;
  const scopeId = `${uid}-scope`;
  const propertyId = `${uid}-property`;
  const previewId = `${uid}-preview`;
  const emailCheckId = `${uid}-email`;
  const singleSearchId = `${uid}-single-search`;
  const resetModalTitleId = `${uid}-reset-title`;
  const resetModalBodyId = `${uid}-reset-body`;
  const logoutModalTitleId = `${uid}-logout-title`;
  const logoutModalBodyId = `${uid}-logout-body`;

  const [task, setTask] = useState("default");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [isEnvFallback, setIsEnvFallback] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [scope, setScope] = useState(SCOPE_OPTIONS[0]);
  const [property, setProperty] = useState(null);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewPeople, setPreviewPeople] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [peopleSearch, setPeopleSearch] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [sendLogoutEmail, setSendLogoutEmail] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [selectedLogin, setSelectedLogin] = useState(null);
  const [singleLogoutOpen, setSingleLogoutOpen] = useState(false);
  const [singleLoggingOut, setSingleLoggingOut] = useState(false);

  const propertySelectOptions = useMemo(
    () =>
      propertyOptions.map((item) => ({
        value: item._id,
        label: item.property_title || item.property_name || item._id,
      })),
    [propertyOptions]
  );

  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserIds.map((id) => String(id))),
    [selectedUserIds]
  );

  const filteredPeople = useMemo(() => {
    const query = peopleSearch.trim().toLowerCase();
    if (!query) {
      return previewPeople;
    }
    return previewPeople.filter((person) => {
      const name = String(person.user_name || "").toLowerCase();
      const email = String(person.email || "").toLowerCase();
      const role = String(person.role_title || "").toLowerCase();
      return (
        name.includes(query) || email.includes(query) || role.includes(query)
      );
    });
  }, [previewPeople, peopleSearch]);

  const visiblePeople = useMemo(() => {
    if (peopleSearch.trim() || previewPeople.length <= PEOPLE_LIST_RENDER_LIMIT) {
      return filteredPeople;
    }
    return [];
  }, [filteredPeople, peopleSearch, previewPeople.length]);

  const selectedCount = selectedUserIds.length;
  const visibleToggleRef = useRef(null);
  const selectedPeople = useMemo(() => {
    const ids = new Set(selectedUserIds.map((id) => String(id)));
    return previewPeople.filter((person) => ids.has(String(person.user_id)));
  }, [previewPeople, selectedUserIds]);

  const loadDefaultPassword = useCallback(async () => {
    try {
      const res = await apiCall("post", apiEndPoints.GETDEFAULTPASSWORD, {});
      if (res?.data?.status === 200) {
        setPassword(res.data.data?.default_user_password || "");
        setConfirmPassword(res.data.data?.default_user_password || "");
        setIsEnvFallback(Boolean(res.data.data?.is_env_fallback));
      } else {
        errorToast(res?.data?.message || strings.initialError);
      }
    } catch (e) {
      errorToast(e?.response?.data?.message || strings.initialError);
    }
  }, []);

  const loadProperties = useCallback(async () => {
    try {
      const res = await apiCall("post", apiEndPoints.GETREPORTPROPERTYLIST, {});
      if (res?.data?.status === 200) {
        setPropertyOptions(handleListResponse(res.data.data) || []);
      } else {
        setPropertyOptions([]);
      }
    } catch (e) {
      setPropertyOptions([]);
    }
  }, []);

  const loadPreview = useCallback(async (selectedScope, selectedProperty) => {
    if (!selectedScope?.value) {
      return;
    }
    setSelectedUserIds([]);
    setLoadingPreview(true);
    try {
      const payload = { scope: selectedScope.value };
      if (selectedProperty?.value) {
        payload.property_id = selectedProperty.value;
      }
      const res = await apiCall(
        "post",
        apiEndPoints.PREVIEWPASSWORDRESET,
        payload
      );
      if (res?.data?.status === 200) {
        const people = Array.isArray(res.data.data?.users)
          ? res.data.data.users
          : [];
        setPreviewCount(res.data.data?.count ?? people.length);
        setPreviewPeople(people);
        setSelectedUserIds([]);
        setPeopleSearch("");
      } else {
        setPreviewCount(null);
        setPreviewPeople([]);
        setSelectedUserIds([]);
        errorToast(res?.data?.message || strings.initialError);
      }
    } catch (e) {
      setPreviewCount(null);
      setPreviewPeople([]);
      setSelectedUserIds([]);
      errorToast(e?.response?.data?.message || strings.initialError);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const loadLoginOptions = useCallback(async (inputValue) => {
    const query = String(inputValue || "").trim();
    if (query.length < 2) {
      return [];
    }
    try {
      const isEmail = query.includes("@");
      const userPayload = {
        limit: 15,
        offset: 0,
        suppressGlobalLoader: true,
      };
      if (isEmail) {
        userPayload.email = query;
      } else {
        userPayload.search_name = query;
      }
      const [usersRes, cpRes] = await Promise.all([
        apiCall("post", apiEndPoints.GETUSERFILTERDATA, userPayload),
        apiCall("post", apiEndPoints.CHANNELPARTNERLIST, {
          search_by_name: query,
          limit: 15,
          offset: 0,
          suppressGlobalLoader: true,
        }),
      ]);
      const users =
        usersRes?.data?.status === 200
          ? handleListResponse(usersRes.data.data) || []
          : [];
      const partners =
        cpRes?.data?.status === 200
          ? handleListResponse(cpRes.data.data) || []
          : [];
      const seen = new Set();
      const options = [];
      [
        ...users.map((row) => buildLoginOption(row, "user")),
        ...partners.map((row) => buildLoginOption(row, "cp")),
      ].forEach((opt) => {
        if (!opt || seen.has(opt.value)) {
          return;
        }
        seen.add(opt.value);
        options.push(opt);
      });
      return options;
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    seo({
      title: strings.accessControl,
      metaDescription: strings.accessControlSubtitle,
      canonicalLink: window.location.href,
    });
    loadDefaultPassword();
    loadProperties();
  }, [loadDefaultPassword, loadProperties]);

  useEffect(() => {
    if (task !== "bulk") {
      return;
    }
    loadPreview(scope, property);
  }, [task, scope, property, loadPreview]);

  useEffect(() => {
    if (!confirmOpen && !logoutConfirmOpen) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      if (resetting || loggingOut) {
        return;
      }
      setConfirmOpen(false);
      setLogoutConfirmOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [confirmOpen, logoutConfirmOpen, resetting, loggingOut]);

  const handleSavePassword = async () => {
    const next = String(password || "").trim();
    const confirm = String(confirmPassword || "").trim();
    if (!next) {
      setPasswordError(strings.defaultPasswordRequired);
      return;
    }
    if (next.length < 6) {
      setPasswordError(strings.defaultPasswordMinLength);
      return;
    }
    if (next !== confirm) {
      setPasswordError(strings.defaultPasswordMismatch);
      return;
    }
    setPasswordError("");
    setSavingPassword(true);
    try {
      const res = await apiCall("post", apiEndPoints.UPDATEDEFAULTPASSWORD, {
        default_user_password: next,
      });
      if (res?.data?.status === 200) {
        successToast(res.data.message);
        setIsEnvFallback(false);
        await loadDefaultPassword();
      } else {
        errorToast(res?.data?.message || strings.initialError);
      }
    } catch (e) {
      errorToast(e?.response?.data?.message || strings.initialError);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleConfirmReset = async () => {
    setResetting(true);
    try {
      const payload = {
        scope: scope.value,
        send_email: sendEmail,
        user_ids: selectedUserIds,
      };
      if (property?.value) {
        payload.property_id = property.value;
      }
      const res = await apiCall(
        "post",
        apiEndPoints.RESETALLUSERPASSWORDS,
        payload
      );
      if (res?.data?.status === 200) {
        successToast(res.data.message);
        setConfirmOpen(false);
        await loadPreview(scope, property);
      } else {
        errorToast(res?.data?.message || strings.initialError);
      }
    } catch (e) {
      errorToast(e?.response?.data?.message || strings.initialError);
    } finally {
      setResetting(false);
    }
  };

  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      const payload = {
        scope: scope.value,
        user_ids: selectedUserIds,
        send_email: sendLogoutEmail,
      };
      if (property?.value) {
        payload.property_id = property.value;
      }
      const res = await apiCall(
        "post",
        apiEndPoints.FORCELOGOUTLOGINS,
        payload
      );
      if (res?.data?.status === 200) {
        successToast(res.data.message);
        setLogoutConfirmOpen(false);
        await loadPreview(scope, property);
      } else {
        errorToast(res?.data?.message || strings.initialError);
      }
    } catch (e) {
      errorToast(e?.response?.data?.message || strings.initialError);
    } finally {
      setLoggingOut(false);
    }
  };

  const handleConfirmSingleLogout = async () => {
    if (!selectedLogin?.value) {
      errorToast(strings.forceLogoutUserRequired);
      return;
    }
    setSingleLoggingOut(true);
    try {
      const res = await apiCall("post", apiEndPoints.FORCELOGOUTUSER, {
        user_id: selectedLogin.value,
      });
      if (res?.data?.status === 200) {
        successToast(res.data.message);
        setSingleLogoutOpen(false);
        setSelectedLogin(null);
      } else {
        errorToast(res?.data?.message || strings.initialError);
      }
    } catch (e) {
      errorToast(e?.response?.data?.message || strings.initialError);
    } finally {
      setSingleLoggingOut(false);
    }
  };

  const confirmBody = strings.confirmCentralResetBody
    .replace("{count}", String(selectedCount))
    .replace("{scope}", scope?.label || "")
    .replace(
      "{property}",
      property?.label
        ? strings.confirmCentralResetProperty.replace(
            "{property}",
            property.label
          )
        : strings.confirmCentralResetAllProperties
    );

  const logoutConfirmBody = strings.forceLogoutConfirmBody
    .replace("{count}", String(selectedCount))
    .replace("{scope}", scope?.label || "")
    .replace(
      "{property}",
      property?.label
        ? strings.confirmCentralResetProperty.replace(
            "{property}",
            property.label
          )
        : strings.confirmCentralResetAllProperties
    );

  const passwordInputType = showPassword ? "text" : "password";
  const bulkDisabled = loggingOut || resetting || selectedCount < 1;

  const togglePerson = (userId) => {
    const id = String(userId);
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const allVisibleSelected =
    visiblePeople.length > 0 &&
    visiblePeople.every((person) =>
      selectedUserIdSet.has(String(person.user_id))
    );
  const someVisibleSelected =
    visiblePeople.some((person) =>
      selectedUserIdSet.has(String(person.user_id))
    ) && !allVisibleSelected;

  useEffect(() => {
    if (!visibleToggleRef.current) {
      return;
    }
    visibleToggleRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);

  const selectAllPeople = () => {
    setSelectedUserIds(previewPeople.map((person) => String(person.user_id)));
  };

  const selectVisiblePeople = () => {
    const next = new Set(selectedUserIds.map((id) => String(id)));
    visiblePeople.forEach((person) => next.add(String(person.user_id)));
    setSelectedUserIds(Array.from(next));
  };

  const clearSelectedPeople = () => {
    setSelectedUserIds([]);
  };

  const toggleVisiblePeople = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(
        visiblePeople.map((person) => String(person.user_id))
      );
      setSelectedUserIds((prev) =>
        prev.filter((id) => !visibleIds.has(String(id)))
      );
      return;
    }
    selectVisiblePeople();
  };

  return (
    <div className="page-body justo-default-password-page">
      <Breadcrumb
        title={strings.accessControl}
        titleactive={strings.accessControl}
      />
      <div className="container-fluid">
        <div className="justo-default-password__card">
          <div className="justo-default-password__top">
            <h1 className="justo-default-password__title">
              {strings.accessControl}
            </h1>
            <p className="justo-default-password__subtitle">
              {strings.accessControlAsk}
            </p>
          </div>

          <div className="justo-default-password__body">
            <div
              className="justo-default-password__tasks"
              role="tablist"
              aria-label={strings.accessControlAsk}
            >
              {TASKS.map((item) => {
                const selected = task === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    className={`justo-default-password__task${
                      selected ? " is-active" : ""
                    }`}
                    onClick={() => setTask(item.id)}
                  >
                    <span
                      className="justo-default-password__task-icon"
                      aria-hidden="true"
                    >
                      <i className={`fa ${item.icon}`} />
                    </span>
                    <span className="justo-default-password__task-copy">
                      <span className="justo-default-password__task-title">
                        {strings[item.titleKey]}
                      </span>
                      <span className="justo-default-password__task-desc">
                        {strings[item.descKey]}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {task === "default" ? (
              <section
                className="justo-default-password__panel"
                role="tabpanel"
                aria-labelledby={`${uid}-default-title`}
              >
                <h2
                  className="justo-default-password__panel-title"
                  id={`${uid}-default-title`}
                >
                  {strings.changeDefaultPassword}
                </h2>
                <p
                  className="justo-default-password__panel-hint"
                  id={passwordHintId}
                >
                  {strings.changeDefaultPasswordHint}
                  {isEnvFallback ? ` ${strings.defaultPasswordUsingEnv}` : ""}
                </p>

                <div className="justo-default-password__grid-2">
                  <div className="justo-default-password__field">
                    <label
                      className="justo-default-password__label"
                      htmlFor={passwordId}
                    >
                      {strings.defaultPassword}
                    </label>
                    <div className="justo-default-password__input-wrap">
                      <input
                        id={passwordId}
                        className="justo-default-password__input"
                        type={passwordInputType}
                        autoComplete="new-password"
                        value={password}
                        aria-describedby={`${passwordHintId}${
                          passwordError ? ` ${passwordErrorId}` : ""
                        }`}
                        aria-invalid={passwordError ? "true" : "false"}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) setPasswordError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSavePassword();
                        }}
                      />
                      <button
                        type="button"
                        className="justo-default-password__reveal"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-pressed={showPassword}
                        aria-label={
                          showPassword
                            ? strings.hidePassword
                            : strings.showPassword
                        }
                      >
                        <i
                          className={`fa ${
                            showPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                  <div className="justo-default-password__field">
                    <label
                      className="justo-default-password__label"
                      htmlFor={confirmId}
                    >
                      {strings.confirmDefaultPassword}
                    </label>
                    <input
                      id={confirmId}
                      className="justo-default-password__input"
                      type={passwordInputType}
                      autoComplete="new-password"
                      value={confirmPassword}
                      aria-invalid={passwordError ? "true" : "false"}
                      aria-describedby={
                        passwordError ? passwordErrorId : undefined
                      }
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (passwordError) setPasswordError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSavePassword();
                      }}
                    />
                  </div>
                </div>

                {passwordError ? (
                  <span
                    className="justo-default-password__error"
                    id={passwordErrorId}
                    role="alert"
                  >
                    {passwordError}
                  </span>
                ) : null}

                <div className="justo-default-password__actions">
                  <button
                    type="button"
                    className="justo-default-password__btn justo-default-password__btn--solid"
                    disabled={savingPassword}
                    onClick={handleSavePassword}
                  >
                    <i className="fa fa-check" aria-hidden="true" />
                    {savingPassword
                      ? strings.saving
                      : strings.saveDefaultPassword}
                  </button>
                </div>
              </section>
            ) : null}

            {task === "bulk" ? (
              <section
                className="justo-default-password__panel"
                role="tabpanel"
                aria-labelledby={`${uid}-bulk-title`}
              >
                <div className="justo-default-password__panel-top">
                  <div>
                    <h2
                      className="justo-default-password__panel-title"
                      id={`${uid}-bulk-title`}
                    >
                      {strings.centralPasswordReset}
                    </h2>
                    <p className="justo-default-password__panel-hint">
                      {strings.centralPasswordResetHint}
                    </p>
                  </div>
                  <div
                    className="justo-default-password__count"
                    id={previewId}
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <span className="justo-default-password__count-label">
                      {strings.affectedLoginsLabel}
                    </span>
                    <strong className="justo-default-password__count-value">
                      {loadingPreview
                        ? "…"
                        : strings.bulkPeopleSelectedCount
                            .replace("{selected}", String(selectedCount))
                            .replace(
                              "{total}",
                              String(previewCount ?? previewPeople.length)
                            )}
                    </strong>
                  </div>
                </div>

                <div className="justo-default-password__grid-2">
                  <div className="justo-default-password__field">
                    <label
                      className="justo-default-password__label"
                      htmlFor={scopeId}
                    >
                      {strings.resetScope}
                    </label>
                    <Select
                      inputId={scopeId}
                      classNamePrefix="justo-default-password-select"
                      options={SCOPE_OPTIONS}
                      value={scope}
                      onChange={(opt) => setScope(opt)}
                      isSearchable={false}
                      aria-label={strings.resetScope}
                      {...selectPortalProps}
                    />
                  </div>
                  <div className="justo-default-password__field">
                    <label
                      className="justo-default-password__label"
                      htmlFor={propertyId}
                    >
                      {strings.resetPropertyOptional}
                    </label>
                    <Select
                      inputId={propertyId}
                      classNamePrefix="justo-default-password-select"
                      options={propertySelectOptions}
                      value={property}
                      onChange={(opt) => setProperty(opt)}
                      isClearable
                      isSearchable
                      placeholder={strings.resetPropertyAllPlaceholder}
                      aria-label={strings.resetPropertyOptional}
                      {...selectPortalProps}
                    />
                  </div>
                </div>

                <div className="justo-default-password__people">
                  <div className="justo-default-password__people-head">
                    <div>
                      <p className="justo-default-password__label">
                        {strings.bulkPeopleLabel}
                      </p>
                      <p className="justo-default-password__people-hint">
                        {strings.bulkPeopleHint}
                      </p>
                    </div>
                    <div className="justo-default-password__people-tools">
                      <button
                        type="button"
                        className="justo-default-password__btn justo-default-password__btn--ghost justo-default-password__btn--sm"
                        disabled={
                          loadingPreview ||
                          !previewPeople.length ||
                          selectedCount === previewPeople.length
                        }
                        onClick={selectAllPeople}
                      >
                        {strings.bulkPeopleSelectAllCount.replace(
                          "{count}",
                          String(previewPeople.length)
                        )}
                      </button>
                      {peopleSearch.trim() && visiblePeople.length > 0 ? (
                        <button
                          type="button"
                          className="justo-default-password__btn justo-default-password__btn--ghost justo-default-password__btn--sm"
                          disabled={loadingPreview || allVisibleSelected}
                          onClick={selectVisiblePeople}
                        >
                          {strings.bulkPeopleSelectVisible.replace(
                            "{count}",
                            String(visiblePeople.length)
                          )}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="justo-default-password__btn justo-default-password__btn--ghost justo-default-password__btn--sm"
                        disabled={loadingPreview || selectedCount < 1}
                        onClick={clearSelectedPeople}
                      >
                        {strings.bulkPeopleClear}
                      </button>
                    </div>
                  </div>
                  <input
                    type="search"
                    className="justo-default-password__input"
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                    placeholder={strings.bulkPeopleSearchPlaceholder}
                    aria-label={strings.bulkPeopleSearchPlaceholder}
                    disabled={loadingPreview || !previewPeople.length}
                  />
                  {loadingPreview ? (
                    <p className="justo-default-password__people-empty">
                      {strings.loadingAffectedCount}
                    </p>
                  ) : !previewPeople.length ? (
                    <p className="justo-default-password__people-empty">
                      {strings.bulkPeopleEmpty}
                    </p>
                  ) : !visiblePeople.length ? (
                    <p className="justo-default-password__people-empty">
                      {peopleSearch.trim()
                        ? strings.bulkPeopleNoSearchMatch
                        : strings.bulkPeopleSearchToBrowse.replace(
                            "{count}",
                            String(previewPeople.length)
                          )}
                    </p>
                  ) : (
                    <ul className="justo-default-password__people-list">
                      <li className="justo-default-password__people-item justo-default-password__people-item--head">
                        <label className="justo-default-password__people-item">
                          <input
                            type="checkbox"
                            ref={visibleToggleRef}
                            checked={allVisibleSelected}
                            onChange={toggleVisiblePeople}
                            aria-label={strings.bulkPeopleToggleVisible}
                          />
                          <span className="justo-default-password__people-copy">
                            <span className="justo-default-password__people-name">
                              {strings.bulkPeopleToggleVisible}
                            </span>
                          </span>
                        </label>
                      </li>
                      {visiblePeople.map((person) => {
                        const id = String(person.user_id);
                        const checked = selectedUserIdSet.has(id);
                        return (
                          <li key={id}>
                            <label className="justo-default-password__people-item">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePerson(id)}
                              />
                              <span className="justo-default-password__people-copy">
                                <span className="justo-default-password__people-name">
                                  {person.user_name || strings.notavailable}
                                </span>
                                <span className="justo-default-password__people-email">
                                  {[person.role_title, person.email]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {selectedCount < 1 && previewPeople.length > 0 ? (
                    <p className="justo-default-password__error" role="status">
                      {strings.bulkPeopleNoneSelected}
                    </p>
                  ) : null}
                </div>

                <div className="justo-default-password__action-grid">
                  <div className="justo-default-password__action-card">
                    <h3 className="justo-default-password__action-title">
                      {strings.resetActionTitle}
                    </h3>
                    <p className="justo-default-password__action-hint">
                      {strings.resetActionHint}
                    </p>
                    <label
                      className="justo-default-password__check"
                      htmlFor={emailCheckId}
                    >
                      <input
                        id={emailCheckId}
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                      />
                      <span>{strings.resetActionEmail}</span>
                    </label>
                    {sendEmail ? <PasswordResetEmailPreview /> : null}
                    <button
                      type="button"
                      className="justo-default-password__btn justo-default-password__btn--danger"
                      disabled={bulkDisabled}
                      onClick={() => setConfirmOpen(true)}
                    >
                      <i className="fa fa-key" aria-hidden="true" />
                      {strings.resetPasswordsNow}
                    </button>
                  </div>

                  <div className="justo-default-password__action-card">
                    <h3 className="justo-default-password__action-title">
                      {strings.logoutActionTitle}
                    </h3>
                    <p className="justo-default-password__action-hint">
                      {strings.logoutActionHint}
                    </p>
                    <label
                      className="justo-default-password__check"
                      htmlFor={`${uid}-logout-email`}
                    >
                      <input
                        id={`${uid}-logout-email`}
                        type="checkbox"
                        checked={sendLogoutEmail}
                        onChange={(e) => setSendLogoutEmail(e.target.checked)}
                      />
                      <span>{strings.logoutActionEmail}</span>
                    </label>
                    {sendLogoutEmail ? <ForceLogoutEmailPreview /> : null}
                    <button
                      type="button"
                      className="justo-default-password__btn justo-default-password__btn--solid"
                      disabled={bulkDisabled}
                      title={strings.forceLogoutHint}
                      onClick={() => setLogoutConfirmOpen(true)}
                    >
                      <i className="fa fa-sign-out" aria-hidden="true" />
                      {strings.forceLogoutNow}
                    </button>
                  </div>
                </div>
              </section>
            ) : null}

            {task === "single" ? (
              <section
                className="justo-default-password__panel"
                role="tabpanel"
                aria-labelledby={`${uid}-single-title`}
              >
                <h2
                  className="justo-default-password__panel-title"
                  id={`${uid}-single-title`}
                >
                  {strings.forceLogoutOneUser}
                </h2>
                <p className="justo-default-password__panel-hint">
                  {strings.forceLogoutOneUserHint}
                </p>

                <div className="justo-default-password__field">
                  <label
                    className="justo-default-password__label"
                    htmlFor={singleSearchId}
                  >
                    {strings.forceLogoutUserSearchLabel}
                  </label>
                  <AsyncSelect
                    inputId={singleSearchId}
                    classNamePrefix="justo-default-password-select"
                    cacheOptions
                    defaultOptions={false}
                    loadOptions={loadLoginOptions}
                    value={selectedLogin}
                    onChange={(opt) => setSelectedLogin(opt)}
                    isClearable
                    isSearchable
                    placeholder={strings.forceLogoutUserSearchPlaceholder}
                    noOptionsMessage={({ inputValue }) =>
                      String(inputValue || "").trim().length < 2
                        ? strings.forceLogoutUserSearchPlaceholder
                        : strings.noDataFound
                    }
                    {...selectPortalProps}
                  />
                </div>

                <div className="justo-default-password__actions">
                  <button
                    type="button"
                    className="justo-default-password__btn justo-default-password__btn--solid"
                    disabled={singleLoggingOut || !selectedLogin?.value}
                    onClick={() => {
                      if (!selectedLogin?.value) {
                        errorToast(strings.forceLogoutUserRequired);
                        return;
                      }
                      setSingleLogoutOpen(true);
                    }}
                  >
                    <i className="fa fa-sign-out" aria-hidden="true" />
                    {strings.forceLogoutOneUserAction}
                  </button>
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>

      <JustoForceLogoutModal
        open={singleLogoutOpen}
        onClose={() => !singleLoggingOut && setSingleLogoutOpen(false)}
        onConfirm={handleConfirmSingleLogout}
        submitting={singleLoggingOut}
        userName={selectedLogin?.userName}
        email={selectedLogin?.email}
        mobile={selectedLogin?.mobile}
      />

      {confirmOpen ? (
        <div
          className="justo-default-password__modal-backdrop"
          onClick={() => !resetting && setConfirmOpen(false)}
          role="presentation"
        >
          <div
            className="justo-default-password__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={resetModalTitleId}
            aria-describedby={resetModalBodyId}
          >
            <button
              type="button"
              className="justo-default-password__modal-close"
              onClick={() => !resetting && setConfirmOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3
              className="justo-default-password__modal-title"
              id={resetModalTitleId}
            >
              {strings.confirmCentralResetTitle}
            </h3>
            <p
              className="justo-default-password__modal-body"
              id={resetModalBodyId}
            >
              {confirmBody}
              {sendEmail
                ? ` ${strings.confirmCentralResetWithEmail}`
                : ` ${strings.confirmCentralResetNoEmail}`}
            </p>
            {selectedPeople.length > 0 ? (
              <div className="justo-default-password__modal-people">
                <p className="justo-default-password__modal-people-label">
                  {strings.bulkConfirmPeopleLabel}
                </p>
                <ul className="justo-default-password__modal-people-list">
                  {selectedPeople.map((person) => (
                    <li key={person.user_id}>
                      <span className="justo-default-password__modal-people-name">
                        {person.user_name || strings.notavailable}
                      </span>
                      <span className="justo-default-password__modal-people-meta">
                        {[person.role_title, person.email]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="justo-default-password__modal-actions">
              <button
                type="button"
                className="justo-default-password__btn justo-default-password__btn--ghost"
                disabled={resetting}
                onClick={() => setConfirmOpen(false)}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="justo-default-password__btn justo-default-password__btn--danger"
                disabled={resetting}
                onClick={handleConfirmReset}
              >
                {resetting ? strings.resetting : strings.confirmReset}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {logoutConfirmOpen ? (
        <div
          className="justo-default-password__modal-backdrop"
          onClick={() => !loggingOut && setLogoutConfirmOpen(false)}
          role="presentation"
        >
          <div
            className="justo-default-password__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={logoutModalTitleId}
            aria-describedby={logoutModalBodyId}
          >
            <button
              type="button"
              className="justo-default-password__modal-close"
              onClick={() => !loggingOut && setLogoutConfirmOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3
              className="justo-default-password__modal-title"
              id={logoutModalTitleId}
            >
              {strings.forceLogoutConfirmTitle}
            </h3>
            <p
              className="justo-default-password__modal-body"
              id={logoutModalBodyId}
            >
              {logoutConfirmBody}
              {sendLogoutEmail
                ? ` ${strings.forceLogoutConfirmWithEmail}`
                : ` ${strings.forceLogoutConfirmNoEmail}`}
            </p>
            {selectedPeople.length > 0 ? (
              <div className="justo-default-password__modal-people">
                <p className="justo-default-password__modal-people-label">
                  {strings.bulkConfirmPeopleLabel}
                </p>
                <ul className="justo-default-password__modal-people-list">
                  {selectedPeople.map((person) => (
                    <li key={person.user_id}>
                      <span className="justo-default-password__modal-people-name">
                        {person.user_name || strings.notavailable}
                      </span>
                      <span className="justo-default-password__modal-people-meta">
                        {[person.role_title, person.email]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="justo-default-password__modal-actions">
              <button
                type="button"
                className="justo-default-password__btn justo-default-password__btn--ghost"
                disabled={loggingOut}
                onClick={() => setLogoutConfirmOpen(false)}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="justo-default-password__btn justo-default-password__btn--solid"
                disabled={loggingOut}
                onClick={handleConfirmLogout}
              >
                {loggingOut
                  ? strings.loggingOut
                  : strings.forceLogoutConfirmAction}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DefaultPassword;
